<template>
  <div class="inline-block">
    <BaseModal v-model="showModal">
      <div class="w-full p-8">
        <h3 class="mb-4">{{ $t("message.marketplaces.publish_to_amazon") }}</h3>

        <div v-if="loading" class="flex justify-center py-8">
          <BaseMiniSpinner />
        </div>

        <div v-else-if="previews.length === 0" class="py-6 text-gray-500">
          {{ $t("message.marketplaces.publish_no_items") }}
        </div>

        <div v-else class="grid grid-cols-1 gap-4">
          <div
            v-for="preview in previews"
            :key="preview.id"
            class="border rounded-md p-4"
          >
            <div class="mb-3">
              <label class="font-bold">
                {{ $t("message.marketplaces.alias") }} (SKU)
              </label>
              <input
                class="regular bordered full"
                type="text"
                v-model="preview.sku"
              />
            </div>

            <!-- Datos precargados que se enviarán a Amazon -->
            <table class="w-full text-sm mb-3">
              <tbody>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">ASIN</td>
                  <td>{{ preview.asin || "—" }}</td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">
                    {{ $t("message.marketplaces.product_title") }}
                  </td>
                  <td>{{ preview.title || "—" }}</td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">Brand</td>
                  <td>{{ preview.brand || "—" }}</td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">Condition</td>
                  <td>{{ preview.condition }}</td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">
                    {{ $t("message.marketplaces.fba") }}
                  </td>
                  <td>{{ preview.fulfillmentChannel }}</td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">Product type</td>
                  <td>{{ preview.productType }}</td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">Price</td>
                  <td>
                    {{ preview.price !== null ? "$" + preview.price : "—" }}
                  </td>
                </tr>
                <tr>
                  <td class="py-1 pr-4 text-gray-500">
                    {{ $t("message.marketplaces.publish_stock") }}
                  </td>
                  <td>
                    <template v-if="preview.fulfillmentChannel === 'FBA'">
                      {{ $t("message.marketplaces.publish_stock_fba") }}
                    </template>
                    <template v-else>
                      {{ preview.stock !== null ? preview.stock : "—" }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Selector de categoría (productType) -->
            <div class="mb-3">
              <label class="font-bold">
                {{ $t("message.marketplaces.publish_category") }}:
                <span class="font-mono">{{ preview.productType }}</span>
              </label>
              <div class="flex flex-row gap-2 mt-1">
                <input
                  class="regular bordered flex-grow"
                  type="text"
                  :placeholder="
                    $t('message.marketplaces.publish_category_search')
                  "
                  v-model="typeSearch[preview.id]"
                  @keyup.enter="searchTypes(preview.id)"
                />
                <button
                  type="button"
                  class="btn btn-dark-button"
                  :disabled="searchingTypes === preview.id"
                  @click="searchTypes(preview.id)"
                >
                  <BaseMiniSpinner v-if="searchingTypes === preview.id" />
                  <span v-else>{{ $t("message.marketplaces.search") }}</span>
                </button>
              </div>
              <div
                v-if="typeResults[preview.id] && typeResults[preview.id].length"
                class="mt-1 border rounded max-h-40 overflow-auto"
              >
                <button
                  v-for="pt in typeResults[preview.id]"
                  :key="pt.name"
                  type="button"
                  class="w-full text-left px-3 py-1 text-sm hover:bg-gray-100"
                  :class="{ 'bg-green-100': preview.productType === pt.name }"
                  @click="selectType(preview, pt.name)"
                >
                  <span class="font-mono">{{ pt.name }}</span>
                  <span class="text-gray-500" v-if="pt.displayName">
                    — {{ pt.displayName }}</span
                  >
                </button>
              </div>
            </div>

            <!-- Descripción editable -->
            <div class="mb-3">
              <label class="font-bold">
                {{ $t("message.marketplaces.publish_description") }}
              </label>
              <textarea
                class="regular bordered full"
                rows="3"
                v-model="preview.description"
              ></textarea>
            </div>

            <!-- Bullet points (uno por línea, máx 5) -->
            <div class="mb-3">
              <label class="font-bold">
                {{ $t("message.marketplaces.publish_bullets") }}
              </label>
              <textarea
                class="regular bordered full"
                rows="5"
                :placeholder="$t('message.marketplaces.publish_bullets_hint')"
                v-model="preview.bulletsText"
              ></textarea>
            </div>

            <!-- Exención de GTIN/UPC: crear ASIN nuevo sin código de barras -->
            <div class="mb-3" v-if="!preview.asin">
              <label class="flex items-center cursor-pointer">
                <input type="checkbox" v-model="preview.gtinExemption" />
                <span class="ml-2 font-bold">
                  {{ $t("message.marketplaces.publish_gtin_exemption") }}
                </span>
              </label>
              <p class="text-xs text-gray-500">
                {{ $t("message.marketplaces.publish_gtin_exemption_hint") }}
              </p>
            </div>

            <!-- Checklist de atributos requeridos por la categoría -->
            <div
              class="mb-3"
              v-if="preview.extraAttrs && preview.extraAttrs.length"
            >
              <label class="font-bold">
                {{ $t("message.marketplaces.publish_required_attrs") }}
              </label>
              <p class="text-xs text-gray-500 mb-1">
                {{ $t("message.marketplaces.publish_required_attrs_hint") }}
              </p>
              <div
                v-for="(attr, ai) in preview.extraAttrs"
                :key="ai"
                class="flex flex-row gap-2 items-center mb-1"
              >
                <span
                  class="font-mono text-xs w-1/2 truncate"
                  :title="attr.name"
                >
                  {{ attr.name }}
                </span>
                <input
                  class="regular bordered flex-grow text-sm"
                  type="text"
                  v-model="attr.value"
                />
              </div>
            </div>

            <!-- Imágenes: selección múltiple + rol por imagen -->
            <div
              class="mb-3"
              v-if="preview.imageRoles && preview.imageRoles.length"
            >
              <label class="font-bold">
                {{ $t("message.marketplaces.publish_images") }}
                ({{ countSelectedImages(preview) }}/{{
                  preview.imageRoles.length
                }})
              </label>
              <!-- Filtro por tipo de imagen -->
              <div class="flex flex-row flex-wrap gap-1 my-1">
                <button
                  type="button"
                  class="px-2 py-0.5 text-xs border rounded"
                  :class="
                    (imageTypeFilter[preview.id] || 'ALL') === 'ALL'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100'
                  "
                  @click="setImageTypeFilter(preview, 'ALL')"
                >
                  {{ $t("message.marketplaces.image_filter_all") }}
                </button>
                <button
                  v-for="t in imageTypes(preview)"
                  :key="t"
                  type="button"
                  class="px-2 py-0.5 text-xs border rounded font-mono"
                  :class="
                    imageTypeFilter[preview.id] === t
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100'
                  "
                  @click="setImageTypeFilter(preview, t)"
                >
                  {{ t }}
                </button>
              </div>
              <div class="flex flex-row flex-wrap gap-3 mt-1">
                <div
                  v-for="(img, i) in visibleImages(preview)"
                  :key="i"
                  class="flex flex-col items-center w-20"
                >
                  <img
                    :src="img.thumbnail || img.url"
                    class="w-16 h-16 object-cover border rounded"
                    :class="{
                      'ring-2 ring-green-500': img.role === 'main',
                      'opacity-40': img.role === 'none'
                    }"
                  />
                  <span class="text-xs text-gray-500 font-mono">{{
                    img.type
                  }}</span>
                  <select
                    class="regular bordered w-full mt-1 text-xs"
                    v-model="img.role"
                  >
                    <option value="none">
                      {{ $t("message.marketplaces.image_role_none") }}
                    </option>
                    <option value="main">
                      {{ $t("message.marketplaces.image_role_main") }}
                    </option>
                    <option value="other">
                      {{ $t("message.marketplaces.image_role_other") }}
                    </option>
                    <option value="swatch">
                      {{ $t("message.marketplaces.image_role_swatch") }}
                    </option>
                  </select>
                </div>
              </div>
              <p
                v-if="
                  countSelectedImages(preview) > 0 &&
                    countMainImages(preview) !== 1
                "
                class="text-xs text-red-500 mt-1"
              >
                {{ $t("message.marketplaces.image_one_main") }}
              </p>
            </div>

            <!-- JSON exacto que se enviará a Amazon (sin camelizar) -->
            <div class="mb-3">
              <button
                type="button"
                class="text-xs text-blue-600 underline"
                @click="toggleJson(preview.id)"
              >
                {{
                  openJson[preview.id]
                    ? $t("message.marketplaces.hide_json")
                    : $t("message.marketplaces.view_json")
                }}
              </button>
              <pre
                v-if="openJson[preview.id]"
                class="mt-2 p-3 bg-gray-900 text-green-200 text-xs rounded overflow-auto max-h-72 whitespace-pre"
                >{{ preview.payloadJson }}</pre
              >
            </div>

            <!-- Errores devueltos por Amazon (atributos requeridos que faltan) -->
            <div
              v-if="
                publishResult &&
                  String(publishResult.id) === String(preview.id) &&
                  publishResult.issues &&
                  publishResult.issues.length
              "
              class="mb-3 p-3 border border-red-300 bg-red-50 rounded"
            >
              <p class="font-bold text-red-700 mb-1">
                {{ $t("message.marketplaces.publish_rejected_title") }}
              </p>
              <ul class="text-sm text-red-700 list-disc pl-5">
                <li v-for="(issue, i) in publishResult.issues" :key="i">
                  {{ issue.message }}
                  <span
                    v-if="issue.attributeNames && issue.attributeNames.length"
                    class="font-mono text-xs text-red-500"
                  >
                    ({{ issue.attributeNames.join(", ") }})
                  </span>
                </li>
              </ul>
            </div>

            <div class="flex justify-end">
              <button
                type="button"
                class="btn btn-success-button flex flex-row items-center"
                :class="{
                  'opacity-50 cursor-not-allowed':
                    publishingId === preview.id || !canPublish(preview)
                }"
                :disabled="publishingId === preview.id || !canPublish(preview)"
                @click="publish(preview)"
              >
                <BaseMiniSpinner
                  v-if="publishingId === preview.id"
                  class="mr-2"
                />
                {{ $t("message.marketplaces.publish") }}
              </button>
            </div>
          </div>
        </div>

        <div class="flex justify-end mt-4">
          <button
            type="button"
            class="btn btn-dark-button"
            @click="showModal = false"
          >
            {{ $t("message.marketplaces.close") }}
          </button>
        </div>
      </div>
    </BaseModal>

    <button class="btn-dark-button my-2 ml-2" @click="open()">
      {{ $t("message.marketplaces.publish_to_amazon") }}
    </button>
  </div>
</template>
<script>
import {
  getAmazonPreview,
  createAmazonListing,
  searchAmazonProductTypes
} from "@/api/store/MarketplacesItemsService.js";

export default {
  name: "ProductMarketplacesPublish",
  props: {
    code: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showModal: false,
      loading: false,
      previews: [],
      publishingId: null,
      openJson: {},
      typeSearch: {},
      typeResults: {},
      searchingTypes: null,
      imageTypeFilter: {},
      publishResult: null
    };
  },
  methods: {
    toggleJson(id) {
      this.$set(this.openJson, id, !this.openJson[id]);
    },
    searchTypes(id) {
      const term = (this.typeSearch[id] || "").trim();
      if (!term || this.searchingTypes) return;
      this.searchingTypes = id;
      searchAmazonProductTypes(term)
        .then(res => {
          this.$set(this.typeResults, id, (res && res.productTypes) || []);
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.searchingTypes = null;
        });
    },
    selectType(preview, name) {
      preview.productType = name;
    },
    countSelectedImages(preview) {
      return (preview.imageRoles || []).filter(i => i.role !== "none").length;
    },
    imageTypes(preview) {
      return [
        ...new Set((preview.imageRoles || []).map(i => i.type).filter(Boolean))
      ];
    },
    visibleImages(preview) {
      const filter = this.imageTypeFilter[preview.id] || "ALL";
      if (filter === "ALL") return preview.imageRoles || [];
      return (preview.imageRoles || []).filter(i => i.type === filter);
    },
    setImageTypeFilter(preview, type) {
      this.$set(this.imageTypeFilter, preview.id, type);
    },
    countMainImages(preview) {
      return (preview.imageRoles || []).filter(i => i.role === "main").length;
    },
    canPublish(preview) {
      if (!preview.sku) return false;
      // Solo si seleccionaste al menos una imagen, debe haber exactamente una principal
      if (this.countSelectedImages(preview) > 0) {
        return this.countMainImages(preview) === 1;
      }
      return true;
    },
    open() {
      this.showModal = true;
      this.loading = true;
      getAmazonPreview(this.code)
        .then(res => {
          this.previews = (res || []).map(p => ({
            ...p,
            // Producto nuevo (sin ASIN): exención de GTIN por defecto para crear
            // el listing sin código de barras (requiere marca con exención).
            gtinExemption: !p.asin,
            // Checklist de atributos requeridos por la categoría: [{name,value}].
            extraAttrs: [],
            // bullets (array) <-> textarea (una por línea)
            bulletsText: (p.bullets || []).join("\n"),
            // imágenes con metadatos (url, type, thumbnail) + rol; por defecto
            // TODAS deseleccionadas ("none"), el usuario elige cuáles y de qué tipo.
            imageRoles: (p.images || []).map(img => ({
              url: img.url,
              type: img.type,
              thumbnail: img.thumbnail,
              role: "none"
            }))
          }));
        })
        .catch(() => {
          this.previews = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Convierte el checklist [{name,value}] en el mapa {name: value} a enviar.
    extraAttrsMap(preview) {
      const map = {};
      (preview.extraAttrs || []).forEach(a => {
        if (a.name && a.value !== "" && a.value !== null) {
          map[a.name] = a.value;
        }
      });
      return map;
    },
    // Tras un rechazo, arma el checklist con los atributos requeridos faltantes.
    buildChecklistFromIssues(preview, issues) {
      // Cubiertos por la exención de GTIN o ya enviados por el backend.
      const skip = new Set([
        "externally_assigned_product_identifier",
        "merchant_suggested_asin",
        "manufacturer",
        "part_number",
        "model_number",
        "bullet_point",
        "list_price",
        "purchasable_offer",
        "fulfillment_availability"
      ]);
      const defaults = {
        is_assembly_required: "false",
        contains_liquid_contents: "false",
        supplier_declared_dg_hz_regulation: "not_applicable",
        number_of_items: "1"
      };
      const existing = new Set((preview.extraAttrs || []).map(a => a.name));
      const rows = preview.extraAttrs ? [...preview.extraAttrs] : [];
      (issues || []).forEach(issue => {
        (issue.attributeNames || []).forEach(name => {
          if (skip.has(name) || existing.has(name)) return;
          existing.add(name);
          rows.push({ name, value: defaults[name] || "" });
        });
      });
      this.$set(preview, "extraAttrs", rows);
    },
    publish(preview) {
      if (this.publishingId) return;
      this.publishingId = preview.id;
      createAmazonListing(this.code, {
        itemId: preview.id,
        sku: preview.sku,
        productType: preview.productType,
        gtinExemption: preview.gtinExemption,
        attributes: this.extraAttrsMap(preview),
        description: preview.description,
        bullets: (preview.bulletsText || "")
          .split("\n")
          .map(b => b.trim())
          .filter(b => b !== ""),
        images: (preview.imageRoles || []).filter(i => i.role !== "none")
      })
        .then(res => {
          const result =
            res && res.results
              ? res.results.find(r => r.id === preview.id)
              : null;
          if (result && result.ok === false) {
            // Amazon rechazó: mostrar los atributos requeridos que faltan.
            this.publishResult = result;
            const errs = (result.issues || []).filter(
              i => i.severity === "ERROR"
            );
            // Genera/actualiza el checklist editable con los faltantes.
            this.buildChecklistFromIssues(preview, errs);
            this.$toast.open({
              message: errs.length
                ? this.$t("message.marketplaces.publish_rejected", {
                    count: errs.length
                  })
                : result.error || this.$t("message.marketplaces.sync_error"),
              type: "error",
              position: "top-right",
              duration: 8000
            });
            return;
          }
          this.publishResult = null;
          const issues = result && result.issues ? result.issues : [];
          if (issues.length) {
            this.$toast.open({
              message: this.$t("message.marketplaces.publish_with_issues", {
                count: issues.length
              }),
              type: "warning",
              position: "top-right"
            });
          } else {
            this.$toast.open({
              message: this.$t("message.marketplaces.publish_success"),
              type: "success",
              position: "top-right"
            });
          }
          this.$emit("published", preview);
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.publishingId = null;
        });
    }
  }
};
</script>
